import type { Rect } from '../world/mapdef';
import { OUTLINE, block, ellipse, rect, shadow, shade, type Ctx } from './paint';
import { dateOf } from '../content/calendar';
import { WEATHER_ICON, weatherFor } from '../content/weather';
import { dayNum } from '../content/calendar';

/** Drawers for the sky package's props (registered from pkgSky). */

const WEEKDAY_ZH = ['日', '一', '二', '三', '四', '五', '六'];

/** Tear-off wall calendar (撕日曆): red header with the month, the big day number, the weekday. */
export function drawCalendar(c: Ctx, r: Rect): void {
  const cx = r.x + r.w / 2;
  const top = r.y - 23;
  const d = dateOf();
  // Nail and string.
  rect(c, OUTLINE, cx - 0.5, top - 2, 1, 2);
  // Pad of pages (a few offset sheets underneath).
  block(c, cx - 7, top + 1, 14, 17, '#e9e2d2', '#f6f1e6', '#cfc6b2');
  block(c, cx - 7, top, 14, 17, '#fffaf0', '#ffffff', '#e6dccb');
  // Header.
  rect(c, '#d0453a', cx - 6.5, top + 0.5, 13, 4.5);
  c.textAlign = 'center';
  c.textBaseline = 'middle';
  c.fillStyle = '#fff3e0';
  c.font = '800 3.6px system-ui, sans-serif';
  c.fillText(`${d.getMonth() + 1}月`, cx, top + 2.9);
  // Day number: red on Sundays, like real calendars.
  c.fillStyle = d.getDay() === 0 ? '#d0453a' : '#1f2a44';
  c.font = '900 8px system-ui, sans-serif';
  c.fillText(String(d.getDate()), cx, top + 9.6);
  c.fillStyle = '#6a5a48';
  c.font = '700 3.2px "PingFang TC", "Noto Sans TC", sans-serif';
  c.fillText(`星期${WEEKDAY_ZH[d.getDay()]}`, cx, top + 14.6);
  // Tiny weather sticker in the corner.
  c.font = '4px "Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji",sans-serif';
  c.fillText(WEATHER_ICON[weatherFor(dayNum()).kind], cx + 5.2, top + 6.8);
}

/** The emergency kit: a red go-bag with a white cross, a water jug and a flashlight. */
export function drawKit(c: Ctx, r: Rect): void {
  const x = r.x, y = r.y + r.h;
  shadow(c, x + r.w / 2 + 2, y, 10, 2.6, 0.3);
  // Water jug.
  block(c, x + r.w - 2, y - 12, 7, 12, '#9fd3ef', '#cdeaf8', '#6fb1d6');
  rect(c, '#e8f6ff', x + r.w - 1, y - 11, 1, 9);
  block(c, x + r.w, y - 14, 3, 2.5, '#3f7fd8');
  // Backpack.
  block(c, x - 1, y - 13, 12, 13, '#d4413a', '#ea6a5e', '#a8302a');
  block(c, x, y - 15, 10, 4, '#b8362f', '#d4413a', '#8e2822');
  rect(c, '#fff', x + 4, y - 10, 2, 6);
  rect(c, '#fff', x + 2, y - 8, 6, 2);
  rect(c, shade('#d4413a', -0.35), x - 1, y - 3, 12, 1);
  // Flashlight lying in front.
  block(c, x + 3, y - 2.5, 7, 3, '#ffcc33', '#ffe27a', '#c99b1d');
  rect(c, '#e8f0f6', x + 10, y - 2.5, 1.5, 3);
}

/** A bucket of folded umbrellas by the door. */
export function drawUmbrellaStand(c: Ctx, r: Rect): void {
  const cx = r.x + r.w / 2, y = r.y + r.h;
  shadow(c, cx, y, 7, 2.2, 0.3);
  const colors = ['#e8504a', '#3f7fd8', '#f2c23a', '#5bb36a', '#b45bc9'];
  colors.forEach((col, i) => {
    const dx = (i - 2) * 2.4;
    const lean = (i - 2) * 0.9;
    const top = y - 22 + Math.abs(i - 2) * 1.5;
    // Folded canopy: a long thin spindle.
    c.fillStyle = OUTLINE;
    c.beginPath();
    c.moveTo(cx + dx + lean - 0.6, top);
    c.lineTo(cx + dx - 2.2, y - 9);
    c.lineTo(cx + dx + 2.2, y - 9);
    c.closePath();
    c.fill();
    c.fillStyle = col;
    c.beginPath();
    c.moveTo(cx + dx + lean, top + 1);
    c.lineTo(cx + dx - 1.5, y - 9.5);
    c.lineTo(cx + dx + 1.5, y - 9.5);
    c.closePath();
    c.fill();
    rect(c, OUTLINE, cx + dx + lean - 0.3, top - 2, 0.8, 2);
    // J-shaped handle peeking out.
    if (i % 2 === 0) {
      c.strokeStyle = '#6b4a2e';
      c.lineWidth = 0.9;
      c.beginPath();
      c.arc(cx + dx + lean + 1.2, top - 2, 1.2, Math.PI, 0, true);
      c.stroke();
    }
  });
  // The bucket.
  block(c, cx - 6, y - 10, 12, 10, '#7f8c99', '#a7b3bf', '#5d6873');
  rect(c, 'rgba(255,255,255,0.25)', cx - 5, y - 9, 1, 8);
  ellipse(c, '#4a535c', cx, y - 10, 5.5, 1.2);
}
